import { Directive, ElementRef, Input, OnChanges, Renderer2, SecurityContext, SimpleChanges } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

import { ALL_ALLOWED_COLORS, ALLOWED_CSS_PROPS } from './../../configs/colors-ionic';

// REGEX: soporta de forma opcional el atributo style="..." dentro de la etiqueta
// Grupo 1: Nombre del color
// Grupo 2: Estilos crudos (opcional)
// Grupo 3: Texto de adentro
const COLOR_TAG_WITH_STYLE = /<(?![iI]\b)([a-z][\w-]*)(?:\s+style="([^"]*)")?>([\s\S]*?)<\/ \1>|<(?![iI]\b)([a-z][\w-]*)(?:\s+style="([^"]*)")?>([\s\S]*?)<\/\4>/gi;

// REGEX DE ICONOS: 
// Grupo 1: color="..."
// Grupo 2: style="..."
// Grupo 3: nombre-icono
const ICON_TAG_WITH_STYLE = /<i(?:\s+color="([^"]*)")?(?:\s+style="([^"]*)")?>([\s\S]*?)<\/i>/gi;

@Directive({ selector: '[translateFormatter]' })
export class TranslateFormatterDirective implements OnChanges {
  @Input('translateFormatter') textToParse = '';

  /** Lista blanca de colores permitidos. Si se pasa, solo estos nombres se procesarán. */
  private allowedColors: string[] = ALL_ALLOWED_COLORS;

  constructor(
    private el: ElementRef<HTMLElement>
    , private renderer: Renderer2
    , private sanitizer: DomSanitizer
  ) { }

  ngOnChanges(changes: SimpleChanges) {
    // Solo renderizar si el valor del texto realmente ha cambiado
    if (changes['textToParse'] && changes['textToParse'].currentValue !== changes['textToParse'].previousValue) {
      this.render();
    }
  }

  private render() {
    const host = this.el.nativeElement;

    // Soporte saltos de línea (\n)
    this.renderer.setStyle(host, 'white-space', 'pre-line');

    // Limpieza eficiente del contenedor
    this.renderer.setProperty(host, 'textContent', '');

    if (!this.textToParse) return;

    let lastIndex = 0;
    let match: RegExpExecArray | null;
    COLOR_TAG_WITH_STYLE.lastIndex = 0;

    while ((match = COLOR_TAG_WITH_STYLE.exec(this.textToParse)) !== null) {
      // SALVAGUARDA: Si por algún error el regex no avanza, rompemos manualmente el bucle para evitar el freeze
      if (match.index === COLOR_TAG_WITH_STYLE.lastIndex) {
        COLOR_TAG_WITH_STYLE.lastIndex++;
      }

      if (match.index > lastIndex) {
        this.processTextOrIcons(host, this.textToParse.slice(lastIndex, match.index));
      }

      // Estructuramos las variables dependiendo de cuál grupo del Regex hizo match
      const color = match[1] || match[4];
      const rawStyles = match[2] || match[5] || '';
      const inner = match[3] || match[6];

      // Validación de lista blanca (Seguridad Extra para el color)
      if (!this.allowedColors.includes(color)) {
        this.processTextOrIcons(host, inner);
      } else {
        // Creamos el elemento nativo <ion-text>
        const ionText = this.renderer.createElement('ion-text');
        this.renderer.setAttribute(ionText, 'color', color);

        // DETALLE TÉCNICO: Forzamos las clases de Ionic para evitar parpadeos visuales en Ionic 7
        this.renderer.addClass(ionText, 'ion-color');
        this.renderer.addClass(ionText, `ion-color-${color}`);

        // Procesamos y aplicamos los estilos CSS inline de forma segura
        if (rawStyles) {
          this.applySafeStyles(ionText, rawStyles);
        }

        this.processTextOrIcons(ionText, inner);
        this.renderer.appendChild(host, ionText); // Se inserta ordenadamente dentro del ion-label
      }
      lastIndex = COLOR_TAG_WITH_STYLE.lastIndex;
    }

    if (lastIndex < this.textToParse.length) {
      this.processTextOrIcons(host, this.textToParse.slice(lastIndex));
    }
  }

  /**
   * Procesa fragmentos de texto buscando etiquetas <i> con soporte de color y styles.
   */
  private processTextOrIcons(parent: any, text: string) {
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    ICON_TAG_WITH_STYLE.lastIndex = 0;

    while ((match = ICON_TAG_WITH_STYLE.exec(text)) !== null) {
      if (match.index > lastIndex) {
        this.appendText(parent, text.slice(lastIndex, match.index));
      }

      // CORRECCIÓN DE TIPADO: Mapeo de grupos de captura del Regex de iconos
      const iconColor = match[1];            // Grupo 1: Atributo color
      const rawIconStyles = match[2] || '';   // Grupo 2: Atributo style 
      const iconName = match[3]?.trim();     // Grupo 3: Texto interno (Nombre del icono)

      if (iconName) {
        const ionIcon = this.renderer.createElement('ion-icon');
        this.renderer.setAttribute(ionIcon, 'name', iconName);

        if (iconColor && this.allowedColors.includes(iconColor)) {
          this.renderer.setAttribute(ionIcon, 'color', iconColor);
        }

        if (rawIconStyles) {
          this.applySafeStyles(ionIcon, rawIconStyles);
        }

        this.renderer.appendChild(parent, ionIcon);
      }

      lastIndex = ICON_TAG_WITH_STYLE.lastIndex;
    }

    if (lastIndex < text.length) {
      this.appendText(parent, text.slice(lastIndex));
    }
  }

  private appendText(parent: any, text: string) {
    this.renderer.appendChild(parent, this.renderer.createText(text));
  }

  /**
   * Procesa la cadena de estilos y los aplica de forma segura usando Renderer2.
   * Esto evita inyecciones maliciosas fuera del atributo style.
   */
  private applySafeStyles(element: any, rawStyles: string) {
    // Separamos los estilos por punto y coma (ej: "font-weight: 700; text-decoration: underline;")
    const stylesArray = rawStyles.split(';');

    stylesArray.forEach(stylePair => {
      const [property, value] = stylePair.split(':');

      // Limpiamos espacios en blanco
      if (property && value) {
        const cleanProperty = property.trim().toLowerCase();
        const cleanValue = value.trim();

        // Lista blanca de propiedades CSS permitidas por seguridad visual (Opcional pero recomendado)
        const allowedCssProps = ALLOWED_CSS_PROPS;

        if (allowedCssProps.includes(cleanProperty)) {

          // 2. SEGURIDAD EXTRA: Sanitización del valor usando el motor de Angular
          // Esto destruye intentos de url(), expresiones reactivas o XSS en los valores
          const safeStyleString = this.sanitizer.sanitize(
            SecurityContext.STYLE,
            `${cleanProperty}: ${cleanValue}`
          );

          if (safeStyleString) {
            // Extraemos el valor ya sanitizado y limpio por Angular
            const secureValue = safeStyleString.replace(`${cleanProperty}:`, '').trim();
            this.renderer.setStyle(element, cleanProperty, secureValue);
          }
        }
      }
    });
  }
}
